// src/pages/auth/Login.tsx
import { logger } from '../../utils/logger';

import {
  IonContent,
  IonPage,
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  IonSpinner,
  IonIcon,
  IonAlert,
  IonToast,
} from '@ionic/react';
import { eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { useState } from 'react';
import { supabase } from '../../services/supabase';
import { rateLimitedLogin } from '../../services/rateLimitedAuthService';
import { Capacitor } from '@capacitor/core';
import './Auth.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Password reset states
  const [showResetAlert, setShowResetAlert] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Use rate-limited login (5 attempts per 15 minutes)
    const result = await rateLimitedLogin(email, password);

    if (result.error) {
      setError(result.error);
      setLoading(false);
    } else {
      // Login successful - auth state change will trigger redirect
      // No need to set loading to false, component will unmount
    }
  };

  const handlePasswordReset = async (emailAddr?: string) => {
    const resetEmailAddr = emailAddr || resetEmail || email;

    if (!resetEmailAddr || !resetEmailAddr.trim()) {
      setToastMessage('Please enter your email address');
      setToastColor('danger');
      setShowToast(true);
      return false;
    }

    setLoading(true);

    try {
      // Use deep link for mobile platforms, web URL for web
      const isNative = Capacitor.isNativePlatform();

      // For web: Use VITE_APP_URL if set (production), otherwise use current origin (local dev)
      const webBaseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

      const redirectUrl = isNative
        ? 'com.dicecat.app://auth/callback?type=recovery'
        : `${webBaseUrl}/reset-password.html`;

      logger.log('🔐 Platform:', isNative ? 'Native (mobile)' : 'Web');
      logger.log('🔐 Web base URL:', isNative ? 'N/A' : webBaseUrl);
      logger.log('🔐 Password reset redirect URL:', redirectUrl);

      const { error } = await supabase.auth.resetPasswordForEmail(
        resetEmailAddr.trim(),
        {
          redirectTo: redirectUrl,
        }
      );

      if (error) throw error;

      setToastMessage('Password reset email sent! Check your inbox.');
      setToastColor('success');
      setShowToast(true);
      setShowResetAlert(false);
      setResetEmail('');
      return false; // Prevent alert from closing automatically
    } catch (error: any) {
      logger.error('Password reset error:', error);
      setToastMessage(error.message || 'Failed to send reset email');
      setToastColor('danger');
      setShowToast(true);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="auth-container">
          {/* Logo and Branding - Centered */}
          <div className="dicecat-branding">
            <img
              src="/assets/dicecat-logo-composite.svg"
              alt="Dice Cat"
              className="dicecat-composite-logo"
            />
            <p className="dicecat-tagline">Connect with board gamers and RPG players online and near you</p>
          </div>

          {/* Welcome Back Header */}
          <div className="auth-header">
            <h2>Welcome Back</h2>
            <p>Sign in to continue your adventure</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin}>
            <IonItem>
              <IonLabel position="stacked">Email</IonLabel>
              <IonInput
                type="email"
                value={email}
                onIonInput={(e) => setEmail(e.detail.value!)}
                required
                placeholder="your@email.com"
                autocomplete="email"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Password</IonLabel>
              <IonInput
                type={showPassword ? 'text' : 'password'}
                value={password}
                onIonInput={(e) => setPassword(e.detail.value!)}
                required
                placeholder="Enter your password"
                autocomplete="current-password"
              />
              <IonIcon
                slot="end"
                icon={showPassword ? eyeOffOutline : eyeOutline}
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: 'var(--ion-color-medium)',
                  marginTop: '20px'
                }}
              />
            </IonItem>

            {/* Forgot Password Link */}
            <div style={{
              textAlign: 'right',
              marginTop: '4px',
              marginBottom: '8px'
            }}>
              <IonButton
                fill="clear"
                size="small"
                onClick={() => {
                  setResetEmail(email);
                  setShowResetAlert(true);
                }}
                disabled={loading}
              >
                Forgot Password?
              </IonButton>
            </div>

            {error && (
              <IonText color="danger">
                <p className="error-text">{error}</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              type="submit"
              disabled={loading}
              className="ion-margin-top"
            >
              {loading ? <IonSpinner name="crescent" /> : 'Login'}
            </IonButton>
          </form>

          {/* Footer */}
          <div className="auth-footer">
            <IonButton
              expand="block"
              fill="clear"
              routerLink="/signup"
              disabled={loading}
            >
              Don't have an account? <strong>&nbsp;Sign Up</strong>
            </IonButton>
          </div>
        </div>

        {/* Password Reset Alert */}
        <IonAlert
          isOpen={showResetAlert}
          onDidDismiss={() => setShowResetAlert(false)}
          header="Reset Password"
          message="Enter your email address and we'll send you a link to reset your password."
          inputs={[
            {
              name: 'email',
              type: 'email',
              placeholder: 'your@email.com',
              value: resetEmail,
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Send Reset Link',
              handler: (data) => {
                handlePasswordReset(data.email);
                return false; // Prevent auto-close
              },
            },
          ]}
        />

        {/* Toast for feedback */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
          position="bottom"
          aria-live="polite"
        />
      </IonContent>
    </IonPage>
  );
};

export default Login;